import { Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { lazyPage } from './lib/lazyPage'
import { recordRoute } from './lib/recentRoutes'
import PageLoader from './components/PageLoader'
import ToastContainer from './components/Toast'
import OrderReadyBanner from './components/OrderReadyBanner'
import RequireAuth from './components/RequireAuth'
import RequirePointsEnabled from './components/RequirePointsEnabled'
import { useIdleTimeout } from './hooks/useIdleTimeout'
import { useLayoutPreference } from './hooks/useLayoutPreference'
import DemoBanner from './components/DemoBanner'
import { MaintenanceGuard } from './components/MaintenanceGuard'
import OrderReadyNotifierMount from './components/OrderReadyNotifierMount'
import StoreEventBanner from './components/StoreEventBanner'
import StoreNotifierMount from './components/StoreNotifierMount'
import { useStaffAuth } from './store/useStaffAuth'
import { getGuestId, hasGuestOrdered } from './lib/guestId'
import { isPlanEnabled, isSelfOrderEnabled, isExperienceEnabled, isLicensePendingDemoEnabled } from './lib/features'
import { useStore } from './store/useStore'
import { useStoreOwner } from './store/useStoreOwner'

// Import all pages
import Splash from './pages/Splash'
import Onboarding from './pages/Onboarding'
import Login from './pages/Login'
import AuthAction from './pages/AuthAction'
import Home from './pages/Home'
import Wallet from './pages/Wallet'
import Charge from './pages/Charge'
import RedeemPointCode from './pages/RedeemPointCode'
import ChargeConfirm from './pages/ChargeConfirm'
import ChargeSuccess from './pages/ChargeSuccess'
// #1188 Phase 2: バーチャル口座振込チャージ
import ChargeMethodPicker from './pages/ChargeMethodPicker'
import ChargeBank from './pages/ChargeBank'
import ChargeRevolut from './pages/ChargeRevolut'
import ChargeLocal from './pages/ChargeLocal'
import ChargeCard from './pages/ChargeCard'
import ChargeReturn from './pages/ChargeReturn'
import NearbyStores from './pages/NearbyStores'
import RideHub from './pages/RideHub'
import StoreDetail from './pages/StoreDetail'
import CourseDetail from './pages/StoreDetail/CourseDetail'
import QRScan from './pages/QRScan'
import Pay from './pages/Pay'
import PaymentComplete from './pages/PaymentComplete'
import Review from './pages/Review'
import TransactionHistory from './pages/TransactionHistory'
import WebReceipt from './pages/WebReceipt'
import Profile from './pages/Profile'
import PublicOrderStatus from './pages/PublicOrderStatus'
import BinderBill from './pages/BinderBill'
import ProfileSetup from './pages/ProfileSetup'
import SecurityPolicy from './pages/SecurityPolicy'
import TravelBudget from './pages/TravelBudget'
import MyReservations from './pages/MyReservations'
import Favorites from './pages/Favorites'
import PublicReservation from './pages/PublicReservation'
import OrderComplete from './pages/OrderComplete'
import PasskeySettings from './pages/PasskeySettings'
import DeviceManagement from './pages/DeviceManagement'
import MyOrders from './pages/MyOrders'
import StampGate from './pages/StampGate'
import PublicExperienceShare from './domains/experience/pages/PublicExperienceShare'
import PublicExperienceTicket from './domains/experience/pages/PublicExperienceTicket'
import StampRallyTop from './pages/StampRally'
import StampEventInfo from './pages/StampRally/EventInfo'
import StampProgramInfo from './pages/StampRally/ProgramInfo'
import StampBoothList from './pages/StampRally/StampBoothList'
import StampBoothDetail from './pages/StampRally/StampBoothDetail'
import StampTicket from './pages/StampRally/StampTicket'
import ArGate from './pages/StampRally/ArGate'
import ArReturn from './pages/StampRally/ArReturn'
import RequireStoreAuth from './components/RequireStoreAuth'
import RequireApprovedOwner from './components/RequireApprovedOwner'
import RequireStoreAccess, { StoreQueryRedirect } from './components/RequireStoreAccess'
import StoreLayout from './components/StoreLayout'

// #1150 店舗オーナー系 / admin 系ページは route 単位で lazy 化し main bundle から
// 外す (顧客は一生開かないページ群)。顧客動線 (決済 / 店探し / チャージ /
// スタンプラリー / プラン / 公開ページ) は初回表示を遅らせないため eager のまま。
// デプロイ跨ぎの chunk 消失時は lazyPage が一度だけリロードして復旧する。
// StoresMap / Delivery は顧客ページだが、leaflet (地図) を main に引き込む
// 唯一の eager 経路だったため lazy 側に置く (決済動線ではない別入口のビュー)。
// #1930 問い合わせ — **ログイン不要の公開ページ**。種別に「ログインできない」が
// ある以上、認証の内側に置いたら要件を満たせない。
const Contact = lazyPage(() => import('./pages/Contact'))
const TrialEnter = lazyPage(() => import('./domains/trial/pages/TrialEnter'))
const TrialPrints = lazyPage(() => import('./domains/trial/pages/TrialPrints'))
const TrialReceipt = lazyPage(() => import('./domains/trial/pages/TrialReceipt'))
// #2004 メールの停止リンクの着地。ログイン不要
const MailUnsubscribe = lazyPage(() => import('./pages/MailUnsubscribe'))
const StoresMap = lazyPage(() => import('./pages/StoresMap'))
// 旅前プランナー / 体験チケットは feature flag 付きの独立フロー (来日前 / 予約時に
// 使うもので、店頭の決済動線ではない)。PublicExperienceShare だけは LINE 共有
// リンクの着地点なので eager に残す。
const PlanTop = lazyPage(() => import('./domains/plan/pages'))
const PlanWizard = lazyPage(() => import('./domains/plan/pages/PlanWizard'))
const PlanSuggest = lazyPage(() => import('./domains/plan/pages/PlanSuggest'))
const PlanEdit = lazyPage(() => import('./domains/plan/pages/PlanEdit'))
const PlanCatalog = lazyPage(() => import('./domains/plan/pages/PlanCatalog'))
const PlanWishlist = lazyPage(() => import('./domains/plan/pages/PlanWishlist'))
const PlanTrip = lazyPage(() => import('./domains/plan/pages/PlanTrip'))
const PlanShared = lazyPage(() => import('./domains/plan/pages/PlanShared'))
const TripWizard = lazyPage(() => import('./domains/plan/pages/TripWizard'))
const TripItinerary = lazyPage(() => import('./domains/plan/pages/TripItinerary'))
// #1665 宿泊代行の申込 (許可待ち機能。isLicensePendingDemoEnabled で route ごと消える)
const StayRequestForm = lazyPage(() => import('./domains/lodging/pages/StayRequestForm'))
const ExperienceBuy = lazyPage(() => import('./domains/experience/pages/ExperienceBuy'))
const TrialExperienceBuy = lazyPage(() => import('./domains/experience/pages/TrialExperienceBuy'))
const ExperienceClaim = lazyPage(() => import('./domains/experience/pages/ExperienceClaim'))
const ExperienceTicketDetail = lazyPage(() => import('./domains/experience/pages/ExperienceTicketDetail'))
const DeliveryLanding = lazyPage(() => import('./pages/Delivery'))
const DeliveryStores = lazyPage(() => import('./pages/Delivery/DeliveryStores'))
const StoreLogin = lazyPage(() => import('./pages/StoreLogin'))
const StaffLogin = lazyPage(() => import('./pages/StaffLogin'))
const StoreStaff = lazyPage(() => import('./pages/StoreStaff'))
const StoreSignup = lazyPage(() => import('./pages/StoreSignup'))
const StoreOnboarding = lazyPage(() => import('./pages/StoreOnboarding'))
const StoreRegister = lazyPage(() => import('./pages/StoreRegister'))
const StoreCashier = lazyPage(() => import('./pages/StoreCashier'))
const StoreDashboard = lazyPage(() => import('./pages/StoreDashboard'))
const StoreReport = lazyPage(() => import('./pages/StoreReport'))
const StoreProfit = lazyPage(() => import('./domains/profit/pages/StoreProfit'))  // #2194 利益の目安
const StoreEcOrder = lazyPage(() => import('./domains/ec/pages/StoreEcOrder'))  // #2612 EC のご注文に答える
const StoreBilling = lazyPage(() => import('./pages/StoreBilling'))
const StoreGuarantee = lazyPage(() => import('./pages/StoreGuarantee'))
const StoreSubscription = lazyPage(() => import('./pages/StoreSubscription'))
const StoreTransactionHistory = lazyPage(() => import('./pages/StoreTransactionHistory'))
const StoreKitchen = lazyPage(() => import('./pages/StoreKitchen'))
const StorePickupCounter = lazyPage(() => import('./pages/StorePickupCounter'))
const PrinterSetupGuide = lazyPage(() => import('./pages/PrinterSetupGuide'))
const PrinterSetupGuideUsb = lazyPage(() => import('./pages/PrinterSetupGuideUsb'))
const StoreQR = lazyPage(() => import('./pages/StoreQR'))
const StoreSettings = lazyPage(() => import('./pages/StoreSettings'))
const StoreReservations = lazyPage(() => import('./pages/StoreReservations'))
const StoreCourses = lazyPage(() => import('./pages/StoreCourses'))
const StoreAllYouCan = lazyPage(() => import('./pages/StoreAllYouCan'))
const StoreExperiences = lazyPage(() => import('./pages/StoreExperiences'))
const StoreExperienceOps = lazyPage(() => import('./pages/StoreExperienceOps'))
const StoreExperienceRedeem = lazyPage(() => import('./domains/experience/pages/StoreExperienceRedeem'))
const StoreRefundRedeem = lazyPage(() => import('./pages/StoreRefundRedeem'))  // #2226 返金の引換券
const StoreOrders = lazyPage(() => import('./pages/StoreOrders'))
const StoreDisputeDetail = lazyPage(() => import('./pages/StoreDisputeDetail'))
const StoreOwnerDisputes = lazyPage(() => import('./pages/StoreOwnerDisputes'))
const StoreRefundIssues = lazyPage(() => import('./pages/StoreRefundIssues'))
const StoreStripeAccount = lazyPage(() => import('./pages/StoreStripeAccount'))
const StoreTableQR = lazyPage(() => import('./pages/StoreTableQR'))
const StoreItemCodes = lazyPage(() => import('./pages/StoreItemCodes'))
const StoreStockReceive = lazyPage(() => import('./pages/StoreStockReceive'))
const StoreStockHistory = lazyPage(() => import('./pages/StoreStockHistory'))
const StoreStocktake = lazyPage(() => import('./pages/StoreStocktake'))
const StoreProductImport = lazyPage(() => import('./pages/StoreProductImport'))
const StoreSuppliers = lazyPage(() => import('./pages/StoreSuppliers'))
const StoreReorderSuggestions = lazyPage(() => import('./pages/StoreReorderSuggestions'))
const StorePreferences = lazyPage(() => import('./pages/StorePreferences'))
// #1554 設定は性質ごとに別ページ。一覧は入口だけにする。
const PrefSecurity = lazyPage(() => import('./pages/StorePreferences/Security'))
const PrefBilling = lazyPage(() => import('./pages/StorePreferences/Billing'))
const PrefSound = lazyPage(() => import('./pages/StorePreferences/Sound'))
const PrefLayout = lazyPage(() => import('./pages/StorePreferences/Layout'))
const StoreDeviceManagement = lazyPage(() => import('./pages/StoreDeviceManagement'))
const StoreTerms = lazyPage(() => import('./pages/StoreTerms'))
const EcVendorTerms = lazyPage(() => import('./pages/EcVendorTerms'))
const AdminLogin = lazyPage(() => import('./pages/AdminLogin'))
const AdminAlerts = lazyPage(() => import('./pages/AdminAlerts'))
const AdminRefunds = lazyPage(() => import('./pages/AdminRefunds'))
const AdminAiBudget = lazyPage(() => import('./pages/AdminAiBudget'))
const AdminEcPaymentLimits = lazyPage(() => import('./pages/AdminEcPaymentLimits'))
const AdminFirestoreBudget = lazyPage(() => import('./pages/AdminFirestoreBudget'))
const AdminShares = lazyPage(() => import('./pages/AdminShares'))
const AdminGrowth = lazyPage(() => import('./pages/AdminGrowth'))
const AdminAccountLookup = lazyPage(() => import('./pages/AdminAccountLookup'))
const AdminCityReport = lazyPage(() => import('./pages/AdminCityReport'))
const AdminPlanReport = lazyPage(() => import('./pages/AdminPlanReport'))
const AdminPlanCatalog = lazyPage(() => import('./pages/AdminPlanCatalog'))
// #2121 代行掲載 (弊社が預かって載せている店の区分・許諾の記録・引き渡し)
const AdminAgencyListings = lazyPage(() => import('./pages/AdminAgencyListings'))
// #2121 代行で預かっている店の編集 (運営専用。店舗オーナーのフローは使わない)
const AdminAgencyStore = lazyPage(() => import('./pages/AdminAgencyStore'))
const AdminAccountDeletionAudit = lazyPage(() => import('./pages/AdminAccountDeletionAudit'))
const AdminAccountPurgeStatus = lazyPage(() => import('./pages/AdminAccountPurgeStatus'))
const AdminAnnouncements = lazyPage(() => import('./pages/AdminAnnouncements'))
const AdminStampDashboard = lazyPage(() => import('./pages/AdminStamp/AdminStampDashboard'))
const AdminStampRegions = lazyPage(() => import('./pages/AdminStamp/AdminStampRegions'))
const AdminStampEvents = lazyPage(() => import('./pages/AdminStamp/AdminStampEvents'))
const AdminStampBooths = lazyPage(() => import('./pages/AdminStamp/AdminStampBooths'))
const AdminStampArSpots = lazyPage(() => import('./pages/AdminStamp/AdminStampArSpots'))
const AdminStampArNotes = lazyPage(() => import('./pages/AdminStamp/AdminStampArNotes'))
const AdminStampPrize = lazyPage(() => import('./pages/AdminStamp/AdminStampPrize'))
const AdminStampLottery = lazyPage(() => import('./pages/AdminStamp/AdminStampLottery'))
const AdminStampBanners = lazyPage(() => import('./pages/AdminStamp/AdminStampBanners'))
const AdminStampQrPrint = lazyPage(() => import('./pages/AdminStamp/AdminStampQrPrint'))
const AdminStampRedeem = lazyPage(() => import('./pages/AdminStamp/AdminStampRedeem'))
const AdminPointCampaigns = lazyPage(() => import('./pages/AdminPoints/AdminPointCampaigns'))
const AdminPointSponsors = lazyPage(() => import('./pages/AdminPoints/AdminPointSponsors'))

// Protected store-owner dashboard routes (#295) — these render inside
// StoreLayout (full-width shell with vertical sidebar on lg+). Form/auth
// pages under /store/* (login, signup, onboarding, register) keep the
// narrow store-container.
import { useStoreRole } from './hooks/useStoreRole'

/**
 * #1662: 注文管理のチャージバックタブは廃止した。既存のブックマークやメールの
 * `?tab=dispute` を切らさないよう、**注文画面を描く前に**専用画面へ転送する。
 *
 * ここで判定するのは、StoreOrders 内でやると注文一覧が一度描かれ Firestore の
 * listener まで張ってから捨てることになるため (画面もちらつく)。
 * オーナー以外は dispute を扱えない (#995) ので転送せず注文一覧に残す。
 */
function StoreOrdersRoute() {
  const { storeId } = useParams()
  const [searchParams] = useSearchParams()
  const role = useStoreRole()
  if (searchParams.get('tab') === 'dispute' && role === 'owner' && storeId) {
    return <Navigate to={`/store/disputes/${storeId}`} replace />
  }
  return <StoreOrders />
}

const STORE_SHELL_PREFIXES = [
  '/store/dashboard',
  '/store/report',
  '/store/transactions',
  '/store/reservations',
  '/store/orders',
  '/store/cashier',
  '/store/staff',
  '/store/tables',
  '/store/item-codes',
  '/store/qr',
  '/store/kitchen',
  '/store/printer-setup',
  '/store/settings',
  '/store/preferences',
  '/store/refund-issues',
  // #1662: 売上取消請求も refund-issues と同じオーナー向け一覧。
  // 登録し忘れると狭い store-form 幅で描かれ、隣の同種画面と幅が揃わない。
  '/store/disputes',
]

export default function App() {
  useIdleTimeout()
  const location = useLocation()
  const { preference } = useLayoutPreference()
  const isStoreShellView = STORE_SHELL_PREFIXES.some(p => location.pathname.startsWith(p))
  // #2140 体験の「レシートの設定と見本」はお店の設定と同じ幅で出す (設定と見本を左右に並べる)。
  // お店側の画面だが、店の通知を二重に鳴らさないよう /store/ の外に置いているので、ここで足す
  const isStoreFormView = (location.pathname.startsWith('/store/') && !isStoreShellView)
    || location.pathname === '/trial/receipt'
  const customerLoggedIn = useStore((s) => s.isLoggedIn && !!s.userId)
  // #2140 ゲスト (卓の QR からログインせずに注文した人) にも「提供しました」を出す。ゲスト ID は
  // スタンプラリー等でも発行されるので、**注文したゲストだけ** (注文していない人には購読を開かない)
  const notifierActive = isSelfOrderEnabled() && (customerLoggedIn || (!!getGuestId() && hasGuestOrdered()))
    && !location.pathname.startsWith('/store/')
  // #968 店舗オーナーがログインしている間は、どの画面でも新規注文/現金会計/カード
  // 決済の通知（音＋バナー）をグローバルに出す。
  const storeOwnerLoggedIn = useStoreOwner((s) => s.isStoreLoggedIn)
  // 店長・店員 (店員ログイン) の端末でも同じ通知を出す。現場の端末はオーナーでは入らない。
  // 店の画面 (/store/) にいるときだけ — 店員ログインの入口 (/trial/enter 等) で、端末に残っていた
  // 前のセッション (失効した店) のまま購読を始めて認証エラーを出さないため
  const staffSessionStoreId = useStaffAuth((s) => (s.session && s.session.exp * 1000 > Date.now() ? s.session.storeId : null))
  const staffStoreId = location.pathname.startsWith('/store/') ? staffSessionStoreId : null

  // #1932 不具合の問い合わせに「どの画面を通ってきたか」を自動で添えるため、
  // 直近に見ていた画面を覚えておく (sessionStorage。アカウントとは紐づけない)。
  useEffect(() => {
    recordRoute(location.pathname)
  }, [location.pathname])

  // Sync body[data-layout] so pages can gate wide-layout-only grid classes
  // via `data-[layout=experiment-1]:...` Tailwind variants.
  useEffect(() => {
    document.body.dataset.layout = preference
    return () => { delete document.body.dataset.layout }
  }, [preference])

  // Bootstrap session restore for entry points that bypass Splash. A
  // customer who scans a table QR lands directly on /stores/X?table=N,
  // never hits Splash, and so isLoggedIn stays false in zustand even
  // though localStorage.isLoggedIn is true from a prior session — that
  // would force a redundant /login round-trip on the very first
  // add-to-cart click. Run restoreSession once on mount when localStorage
  // says we're authed but the store doesn't reflect it yet.
  const isLoggedIn = useStore((s) => s.isLoggedIn)
  const restoreSession = useStore((s) => s.restoreSession)
  useEffect(() => {
    if (isLoggedIn) return
    if (typeof window === 'undefined') return
    if (localStorage.getItem('isLoggedIn') !== 'true') return
    restoreSession().catch((err) => {
      console.warn('[App] background restoreSession failed:', err)
    })
  }, [isLoggedIn, restoreSession])

  // All store-shell pages use the wide `store-shell` outer container so
  // StoreLayout can control the inner max-width responsively (mobile 430px
  // → tablet 640px → PC 768px in Standard; full-width + sidebar in
  // experiment-1). Putting mobile-container's hard 430px cap outside would
  // defeat the responsive widening in Standard mode.
  const containerClass = isStoreShellView
    ? 'store-shell'
    : isStoreFormView
      ? 'store-container shadow-lg'
      : 'mobile-container shadow-lg'

  return (
    <div className={containerClass}>
      <DemoBanner />
      <ToastContainer />
      <OrderReadyBanner />
      {notifierActive && <OrderReadyNotifierMount />}
      <StoreEventBanner />
      {(storeOwnerLoggedIn || staffStoreId) && <StoreNotifierMount staffStoreId={staffStoreId} />}
      {/* #667: Stripe degraded 時に全 route を maintenance page で覆う */}
      <MaintenanceGuard>
      {/* #1150: lazy route の chunk ロード中に画面が真っ白にならないための fallback */}
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Splash />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/login" element={<Login />} />
        {/* #850: Firebase Auth カスタム action handler — メール内リンクから
            未ログイン状態で到達するため public route。Firebase Console の
            「アクション URL のカスタマイズ」で全テンプレの URL をこの
            パスに変更する運用 (docs/infra-manual-operations.md 参照)。 */}
        <Route path="/auth/action" element={<AuthAction />} />

        {/* Auth-required routes */}
        <Route path="/setup/profile" element={<RequireAuth><ProfileSetup /></RequireAuth>} />
        {/* #1246: ホームはゲスト閲覧可 (残高カード相当は登録導線に差し替え)。
          * allowGuest でも session 復元待ち + ログイン済みの device gate は効く */}
        <Route path="/home" element={<RequireAuth allowGuest><Home /></RequireAuth>} />
        {/* #1246: ウォレットもタブとして開けるゲスト版あり (中で登録導線) */}
        <Route path="/wallet" element={<RequireAuth allowGuest><Wallet /></RequireAuth>} />
        <Route path="/charge" element={<RequireAuth><RequirePointsEnabled><Charge /></RequirePointsEnabled></RequireAuth>} />
        {/* #1433 スポンサーポイントをコードで受け取る */}
        <Route path="/points/redeem" element={<RequireAuth><RequirePointsEnabled><RedeemPointCode /></RequirePointsEnabled></RequireAuth>} />
        <Route path="/charge/method" element={<RequireAuth><RequirePointsEnabled><ChargeMethodPicker /></RequirePointsEnabled></RequireAuth>} />
        <Route path="/charge/bank" element={<RequireAuth><RequirePointsEnabled><ChargeBank /></RequirePointsEnabled></RequireAuth>} />
        <Route path="/charge/revolut" element={<RequireAuth><RequirePointsEnabled><ChargeRevolut /></RequirePointsEnabled></RequireAuth>} />
        <Route path="/charge/local" element={<RequireAuth><RequirePointsEnabled><ChargeLocal /></RequirePointsEnabled></RequireAuth>} />
        <Route path="/charge/card" element={<RequireAuth><RequirePointsEnabled><ChargeCard /></RequirePointsEnabled></RequireAuth>} />
        {/* #1189 Phase 3: /charge/return は Stripe redirect の戻り先。
             query に payment_intent_client_secret が入ってくるが auth 保持のため
             RequireAuth の下に置く (localhost redirect 挙動を CI で検証)。 */}
        <Route path="/charge/return" element={<RequireAuth><RequirePointsEnabled><ChargeReturn /></RequirePointsEnabled></RequireAuth>} />
        <Route path="/charge/confirm" element={<RequireAuth><RequirePointsEnabled><ChargeConfirm /></RequirePointsEnabled></RequireAuth>} />
        <Route path="/charge/success" element={<RequireAuth><RequirePointsEnabled><ChargeSuccess /></RequirePointsEnabled></RequireAuth>} />
        <Route path="/history" element={<RequireAuth><TransactionHistory /></RequireAuth>} />
        <Route path="/receipt/:transactionId" element={<RequireAuth><WebReceipt /></RequireAuth>} />
        {/* #1207 公開レシート — 店側の決済完了 QR から。HMAC トークン (?t=) 認可、
            アカウント無し客もスマホで開けるため RequireAuth を掛けない */}
        <Route path="/r/:transactionId" element={<WebReceipt />} />
        {/* #1217 受け取り状況 — レシートの QR から開く。アプリも会員登録も不要で
            「自分の番号が呼ばれたか」だけが分かる */}
        <Route path="/o/:orderId" element={<PublicOrderStatus />} />
        {/* #1930 問い合わせ。ログイン不要 (「ログインできない」を受けるため)。
          * 店舗の方も `?from=store` で同じ画面を使う。 */}
        <Route path="/contact" element={<Contact />} />
        {/* #1246: 店探し (一覧/地図) はゲスト開放 — QR 直リンクしか入口が無いと
          * 旅行者のファーストタッチが成立しない。データ元 GET /stores/nearby は
          * 元から public。注文送信・決済・予約確定は各画面で従来どおり login 要求 */}
        <Route path="/stores" element={<NearbyStores />} />
        <Route path="/stores/map" element={<StoresMap />} />
        {/* #1619 タクシー配車ハブ (外部リンク集 + アプリ内配車の準備中プレビュー)。public */}
        <Route path="/ride" element={<RideHub />} />
        {/* Store detail is public (guest-viewable via QR code) — payment button requires login */}
        <Route path="/stores/:id" element={<StoreDetail />} />
        <Route path="/stores/:id/courses/:courseId" element={<CourseDetail />} />
        {/* #2039 バインダーに貼った QR から開く、お客様の伝票 (今の金額)。ログイン不要 —
          * 認可は QR のトークン (?t=) だけで、番号では読めない */}
        <Route path="/stores/:id/binder" element={<BinderBill />} />
        {/* #1870 **ゲストでも開ける。** スタンプラリーはゲスト参加できるのに、
          * スタンプを押す唯一の手段であるスキャナがログイン必須だと、ゲストは
          * 「参加できるが 1 個も押せない」状態になる。読み取った先の口が
          * それぞれ自前で認可するので、スキャナ自体を閉じておく意味は無い
          * (決済の QR を読んでも支払い画面がログインを要求する)。 */}
        <Route path="/scan" element={<RequireAuth allowGuest><QRScan /></RequireAuth>} />
        {/* #1126 スタンプラリー: 閲覧系は public（会場ポスターQRから未ログイン
          * 流入した来場者にまずイベントを見せる — StoreDetail の guest-viewable と
          * 同じ思想）。スタンプ付与・チケットはサーバ側で認証を要求する。 */}
        <Route path="/stamp-rally" element={<StampRallyTop />} />
        {/* イベント詳細は**開催前でも読める**。告知が開催のひと月前から出るため、
            ここを開催中だけにすると告知から来た人が行き止まりになる。 */}
        <Route path="/stamp-rally/event/:eventId" element={<StampEventInfo />} />
        <Route path="/stamp-rally/program/:programId" element={<StampProgramInfo />} />
        <Route path="/stamp-rally/stores" element={<StampBoothList />} />
        <Route path="/stamp-rally/stores/:boothId" element={<StampBoothDetail />} />
        {/* #2111 **ゲストもチケットを開ける。** サーバは verify_customer_or_guest で
            ゲストを通すのに、ここだけが弾いていたため、ゲストで完走した人が
            特典を表示しようとするとログインに飛ばされていた (当日に発生)。
            発行条件の判定は常にサーバ側。ID を何も持たない人は進捗の取得が
            401 になり、画面が TOP へ戻す。 */}
        <Route path="/stamp-rally/ticket" element={<StampTicket />} />
        {/* AR宝探し (#1154): マーカーQR着地 / WebAR 復帰。auth は各ページが StampGate 同様に自前解決 */}
        <Route path="/stamp-rally/ar" element={<ArGate />} />
        <Route path="/stamp-rally/ar/return" element={<ArReturn />} />
        {/* ブースQRの着地点 (標準カメラ/アプリ内スキャナ両経路)。Pay.tsx 同様に
          * 内部で auth 解決するが、未ログインでも店リンク遷移は成立させる。 */}
        <Route path="/stamp" element={<StampGate />} />
        {/* #1237 旅前プランナー: 入力〜提案〜編集はゲスト体験可 (StoreDetail の
          * guest-viewable と同思想 — 来日前のユーザーはアカウント未作成が多い)。
          * 下書きは localStorage、保存・旅中モードのみ auth を要求する。
          * VITE_PLAN_ENABLED 未設定/false で route ごと消える (backend 404 と二重ガード)。 */}
        {isPlanEnabled() && (
          <>
            <Route path="/plan" element={<PlanTop />} />
            {/* #1247 Phase A: 全体行程 (出発地→中継地→到着地)。ゲスト可 */}
            <Route path="/plan/route" element={<TripWizard />} />
            {/* #1318 A: 保存前のルート概要 = しおり(TripItinerary)の draft モード。編集を1画面に集約 */}
            <Route path="/plan/route/overview" element={<TripItinerary draftMode />} />
            <Route path="/plan/new" element={<PlanWizard />} />
            <Route path="/plan/suggest" element={<PlanSuggest />} />
            <Route path="/plan/edit" element={<PlanEdit />} />
            <Route path="/plan/catalog" element={<PlanCatalog />} />
            {/* #1290 行きたいリスト (S1)。ゲスト可 (localStorage 永続) */}
            <Route path="/plan/wishlist" element={<PlanWishlist />} />
            <Route path="/plan/trip/:planId" element={<RequireAuth><PlanTrip /></RequireAuth>} />
            {/* #1243: 共有リンクの公開閲覧 (未ログイン可・PII 除外版)。/rsv /e と同じ思想 */}
            <Route path="/plan/s/:shareCode" element={<PlanShared />} />
            {/* #1273 Phase C: 全体しおり (親 trip)。要ログイン */}
            <Route path="/plan/itinerary/:tripId" element={<RequireAuth><TripItinerary /></RequireAuth>} />
            {/* #1665 宿泊代行の申込。**許可待ち機能**なので gate を重ねる —
                画面 (isLicensePendingDemoEnabled) + backend (LODGING_ENABLED で 404) */}
            {isLicensePendingDemoEnabled() && (
              <Route path="/lodging/request" element={<RequireAuth><StayRequestForm /></RequireAuth>} />
            )}
          </>
        )}
        {/* Public entry point for payment QR (HTTPS). Handles auth redirect internally. */}
        <Route path="/pay/:storeId" element={<Pay />} />
        <Route path="/payment/complete" element={<RequireAuth><PaymentComplete /></RequireAuth>} />
        <Route path="/review" element={<RequireAuth><Review /></RequireAuth>} />
        {/* #1246: プロフィールもゲスト版あり (表示名は localStorage・言語設定・登録導線) */}
        <Route path="/profile" element={<RequireAuth allowGuest><Profile /></RequireAuth>} />
        <Route path="/security" element={<RequireAuth><SecurityPolicy /></RequireAuth>} />
        {/* #734: WebAuthn / Passkey 設定画面 */}
        <Route path="/profile/passkeys" element={<RequireAuth><PasskeySettings /></RequireAuth>} />
        <Route path="/profile/devices" element={<RequireAuth><DeviceManagement /></RequireAuth>} />
        <Route path="/travel-budget" element={<RequireAuth><TravelBudget /></RequireAuth>} />
        <Route path="/my-reservations" element={<RequireAuth><MyReservations /></RequireAuth>} />
        {/* #1248 体験チケット: 購入 (要ログイン) / 予約一覧 / QR 詳細 */}
        {isExperienceEnabled() && (
          <>
            <Route path="/experience/buy/:productId" element={<RequireAuth><ExperienceBuy /></RequireAuth>} />
            {/* #2140 体験の店 (紹介ページの体験) の予約。ログインなし (ゲスト) で、当日払いだけ。
                体験の店でなければページが断る。本物の店の予約は上 (ログイン必須) のまま */}
            <Route path="/trial/experience/buy/:productId" element={<RequireAuth allowGuest><TrialExperienceBuy /></RequireAuth>} />
            {/* 現地で買ったチケットを自分のものにする (店が販売直後に出す QR の着地点)。
                支払いは済んでいるので、ここでお金は動かない。 */}
            <Route path="/experience/claim/:code" element={<RequireAuth><ExperienceClaim /></RequireAuth>} />
            {/* #1291: 単独の体験の予約一覧は予約履歴に統合したため廃止 → リダイレクト */}
            <Route path="/my-tickets" element={<Navigate to="/my-reservations" replace />} />
            <Route path="/my-tickets/:ticketId" element={<RequireAuth><ExperienceTicketDetail /></RequireAuth>} />
          </>
        )}
        <Route path="/favorites" element={<RequireAuth><Favorites /></RequireAuth>} />
        {/* #746 公開予約共有ページ — 未ログインで PII 除外版が見える (= 幹事から共有された LINE 友人向け) */}
        {/* #1261: /r/:transactionId (公開レシート, 上で先勝ち) にシャドウされて
          * 到達不能だったため /rsv/ prefix へ分離。旧 /r/{shortCode} 共有 URL は
          * バグにより一度も機能していないので互換リダイレクトは不要 */}
        <Route path="/rsv/:shortCode" element={<PublicReservation />} />
        {/* #1291 体験チケットの公開共有 — 未ログインで予約内容の要約 (PII/code 除外) が見える */}
        <Route path="/e/:shareCode" element={<PublicExperienceShare />} />
        {/* #1248 体験チケットの公開券面 — 購入完了メールの着地点 (未ログイン・印刷可)。
            認可は code の所持 (発行時採番 ≈50bit = 消し込みと同じ bearer トークン) */}
        <Route path="/t/:code" element={<PublicExperienceTicket />} />
        {/* #1005 follow-up: 公開の注文完結。伝票 QR(URL型) をスタッフがスマホ標準
            カメラで開く → token だけで提供/配送完了（ログイン不要）。 */}
        <Route path="/complete" element={<OrderComplete />} />
        <Route path="/mail/unsubscribe" element={<MailUnsubscribe />} />
        {/* #1398 ゲスト注文の受け皿。注文した客が自分の伝票と「お会計」に
            辿り着けないと、ゲストのまま注文できても意味がない。 */}
        <Route path="/my-orders" element={<RequireAuth allowGuest><MyOrders /></RequireAuth>} />
        {/* #284 出前 — separate from /stores so customers don't accidentally
         * start a delivery order while browsing dine-in candidates. */}
        <Route path="/delivery" element={<RequireAuth><DeliveryLanding /></RequireAuth>} />
        <Route path="/delivery/stores" element={<RequireAuth><DeliveryStores /></RequireAuth>} />

        {/* Store owner auth (public) */}
        <Route path="/store/login" element={<StoreLogin />} />
        <Route path="/staff/login" element={<StaffLogin />} />
        {/* #2140 体験のお店側の入口。紹介ページが発行した体験用アカウントを受け取る */}
        <Route path="/trial/enter" element={<TrialEnter />} />
        {/* #2140 体験の店で印刷されるはずだった紙。体験用アカウントのセッションで読む (サーバが体験の店だけに絞る) */}
        <Route path="/trial/prints" element={<TrialPrints />} />
        {/* #2140 レシートの設定と見本 (その人の体験の店にだけ保存) */}
        <Route path="/trial/receipt" element={<TrialReceipt />} />
        <Route path="/store/signup" element={<StoreSignup />} />

        {/* KYC onboarding gate — login required but not yet approved */}
        <Route path="/store/onboarding" element={<RequireStoreAuth><StoreOnboarding /></RequireStoreAuth>} />

        {/* Store owner protected routes — require both login AND Stripe Connect approval */}
        <Route path="/store/register" element={<RequireStoreAuth><RequireApprovedOwner><StoreRegister /></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #1910 店の編集は StoreLayout を通らないので、門を直接かける */}
        <Route path="/store/register/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><RequireStoreAccess><StoreRegister /></RequireStoreAccess></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #1178 レジ会計画面 — 会計伝票/バインダーQRのスキャン着地 (スタッフも使うため StoreLayout 常駐フック付き) */}
        <Route path="/store/cashier/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreCashier /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/dashboard" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreDashboard /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #1248 体験チケット消し込み (店舗スタッフ) */}
        {isExperienceEnabled() && (
          <Route path="/store/experience/redeem" element={<RequireStoreAuth><RequireApprovedOwner><StoreExperienceRedeem /></RequireApprovedOwner></RequireStoreAuth>} />
        )}
        {/* #1292 体験チケット設定 (店舗オーナー: 商品・枠・定員) */}
        {isExperienceEnabled() && (
          <Route path="/store/experiences/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreExperiences /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        )}
        {/* #1292 Phase2 体験の当日運営 (現地販売・空き状況・オンライン締切) */}
        {isExperienceEnabled() && (
          <Route path="/store/experience/operations/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreExperienceOps /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        )}
        <Route path="/store/dashboard/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreDashboard /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #1910 店 ID 無しは既定の店 (store_001) を開いていた。?store= は /:storeId へ、無ければダッシュボードへ */}
        <Route path="/store/report" element={<RequireStoreAuth><RequireApprovedOwner><StoreQueryRedirect base="/store/report" /></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/report/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreReport /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #2194 利益の目安。オーナーだけ (画面も API も店長・スタッフは弾く) */}
        <Route path="/store/profit/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreProfit /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* オーナー単位の請求。店舗 ID を取らないのは、Stripe の連結アカウントも
          * クォータもオーナー単位で、複数店舗でも明細が 1 本になるため。 */}
        <Route path="/store/billing" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreBilling /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #1655 チャージバック保証の加入フロー（**未解禁**）。年間契約で上限も
          * あるので、収支画面の確認ダイアログではなく専用画面で同意を取る。
          * 画面側 gate は StoreGuarantee 内（gate OFF なら /store/billing へ戻す）。 */}
        <Route path="/store/guarantee" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreGuarantee /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #1689 有料プランの申込み。**未解禁** — 画面側は state が取れなければ
          * 案内だけ出して戻す (backend は SAAS_SUBSCRIPTION_ENABLED で 404)。 */}
        <Route path="/store/subscription" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreSubscription /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/transactions/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreTransactionHistory /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/qr" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreQR /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/qr/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreQR /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/kitchen" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreKitchen /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/kitchen/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreKitchen /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #1215 受け取りカウンター (店頭受け取りの呼び出し表示) */}
        <Route path="/store/pickup/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StorePickupCounter /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/printer-setup" element={<RequireStoreAuth><RequireApprovedOwner><PrinterSetupGuide /></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/printer-setup-usb" element={<RequireStoreAuth><RequireApprovedOwner><PrinterSetupGuideUsb /></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/settings" element={<RequireStoreAuth><RequireApprovedOwner><StoreQueryRedirect base="/store/settings" /></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/settings/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreSettings /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #2612 お店あてのメールのリンク (注文ごとに決まった 1 本)。権限 (オーナー・店長が答える / バイトは見るだけ) は
            サーバが注文の出店者から決める (URL に店の id は要らない) */}
        <Route path="/store/ec-orders/:orderId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreEcOrder /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/reservations/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreReservations /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #2226 返金の引換券を読み取る。引き換えが通ってから払う (オーナー・店長。API も 403) */}
        <Route path="/store/refund-redeem/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreRefundRedeem /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/courses/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreCourses /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #1353 放題プラン（飲み放題・食べ放題） */}
        <Route path="/store/all-you-can/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreAllYouCan /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/orders/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreOrdersRoute /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/staff/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreStaff /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/disputes" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreOwnerDisputes /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/refund-issues" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreRefundIssues /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #1662 店舗単位の一覧。全店舗版 (/store/disputes) はオーナーの
            店舗一覧トップからのみ開く — 店舗の文脈で全店舗が出るのを防ぐ */}
        <Route path="/store/disputes/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreOwnerDisputes /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/disputes/:storeId/:paymentIntentId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreDisputeDetail /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/stripe-account" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreStripeAccount /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/tables/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreTableQR /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/item-codes/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreItemCodes /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #1365 在庫オペレーション: 入荷 (検品) と入出庫履歴 */}
        <Route path="/store/stock-receive/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreStockReceive /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/stock-history/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreStockHistory /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/stocktake/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreStocktake /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #1379 商品の初期一括登録 (CSV / 写真) */}
        <Route path="/store/product-import/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreProductImport /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #1379 PR B 仕入先マスタ / 発注点の自動提案 */}
        <Route path="/store/suppliers/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreSuppliers /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/reorder-suggestions/:storeId" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreReorderSuggestions /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/preferences" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StorePreferences /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/preferences/security" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><PrefSecurity /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/preferences/billing" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><PrefBilling /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/preferences/sound" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><PrefSound /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        <Route path="/store/preferences/layout" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><PrefLayout /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #867 v3+: 店舗オーナー登録端末 自己管理画面。RequireStoreAuth は本 path で gate を skip */}
        <Route path="/store/settings/devices" element={<RequireStoreAuth><RequireApprovedOwner><StoreLayout><StoreDeviceManagement /></StoreLayout></RequireApprovedOwner></RequireStoreAuth>} />
        {/* #1657 加盟店規約の閲覧。KYC 前 (onboarding 中) でも読めるよう
            RequireApprovedOwner は掛けない。未同意ブロックはこのパスを通す。 */}
        <Route path="/store/terms" element={<RequireStoreAuth><StoreTerms /></RequireStoreAuth>} />
        {/* #1710 EC 出店規約 + MoU テンプレの閲覧。**ログイン不要** — 出店を検討する店や
            弁護士が Web 上で読めるようにする。ログイン中の EC 出店者には同意ボタンも出る。 */}
        <Route path="/store/ec-terms" element={<EcVendorTerms />} />

        {/* #52 platform admin: distinct auth flow from store owner login.
          * /admin/login uses Google Sign-In (inherits Google 2FA) and the
          * page itself gates via useAdmin hook (server checks ADMIN_EMAILS).
          * Not wrapped in RequireStoreAuth — admin is NOT a store owner. */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/alerts" element={<AdminAlerts />} />
        {/* #2024 返金が失敗している予約 (自動の再試行でも直らなかったものだけ) */}
        <Route path="/admin/refunds" element={<AdminRefunds />} />
        <Route path="/admin/ai-budget" element={<AdminAiBudget />} />
        {/* #2338 EC の決済上限 (超えると EC 全体が止まる) */}
        <Route path="/admin/ec-payment-limits" element={<AdminEcPaymentLimits />} />
        <Route path="/admin/firestore-budget" element={<AdminFirestoreBudget />} />
        <Route path="/admin/shares" element={<AdminShares />} />
        {/* #1568 登録・退会の推移。規模感を見る場所が無かった。 */}
        <Route path="/admin/growth" element={<AdminGrowth />} />
        {/* #1568 本人照会。メールから uid を割り出す (氏名は出さない)。 */}
        <Route path="/admin/account-lookup" element={<AdminAccountLookup />} />
        <Route path="/admin/city-report" element={<AdminCityReport />} />
        {/* #1244 誘客レポート (旅前プランナー → 来訪・決済のファネル) */}
        <Route path="/admin/plan-report" element={<AdminPlanReport />} />
        {/* #1242 プランカタログ管理 (スポット/テンプレ CRUD + AI ドラフト→承認) */}
        <Route path="/admin/plan-catalog" element={<AdminPlanCatalog />} />
        <Route path="/admin/agency-listings" element={<AdminAgencyListings />} />
        <Route path="/admin/agency-listings/:storeId" element={<AdminAgencyStore />} />
        <Route path="/admin/account-deletion-audit" element={<AdminAccountDeletionAudit />} />
        <Route path="/admin/account-purge-status" element={<AdminAccountPurgeStatus />} />
        {/* #1126 スタンプラリー管理 (フルCRUD)。各ページは AdminStampShell 経由で
          * useAdmin 自己ゲート。dashboard/redeem/qr-print は運用系 (Phase9)。 */}
        <Route path="/admin/stamp-rally" element={<AdminStampDashboard />} />
        {/* #1445 還元施策 / #1433 スポンサー */}
        <Route path="/admin/points/campaigns" element={<AdminPointCampaigns />} />
        <Route path="/admin/points/sponsors" element={<AdminPointSponsors />} />
        {/* #1384 旅先で役に立つお知らせ枠の運営管理 */}
        <Route path="/admin/announcements" element={<AdminAnnouncements />} />
        <Route path="/admin/stamp-rally/regions" element={<AdminStampRegions />} />
        <Route path="/admin/stamp-rally/redeem" element={<AdminStampRedeem />} />
        <Route path="/admin/stamp-rally/events/:eventId/qr-print" element={<AdminStampQrPrint />} />
        <Route path="/admin/stamp-rally/events" element={<AdminStampEvents />} />
        <Route path="/admin/stamp-rally/events/:eventId/booths" element={<AdminStampBooths />} />
        <Route path="/admin/stamp-rally/events/:eventId/ar-spots" element={<AdminStampArSpots />} />
        {/* AR の掲示板のメモ: 受付の設定と、承認・非表示 */}
        <Route path="/admin/stamp-rally/events/:eventId/ar-notes" element={<AdminStampArNotes />} />
        <Route path="/admin/stamp-rally/events/:eventId/prize" element={<AdminStampPrize />} />
        {/* #1871 参加抽選。完走報酬 (prize) とは別の在庫・別の設定 */}
        <Route path="/admin/stamp-rally/events/:eventId/lottery" element={<AdminStampLottery />} />
        <Route path="/admin/stamp-rally/events/:eventId/banners" element={<AdminStampBanners />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </MaintenanceGuard>
    </div>
  )
}
